import { useLazyQuery, useQuery } from "@apollo/client/react";
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import 'moment/locale/es';
import { useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { adaptDelegacionReclamosArray } from '../../../adapters/delegacionAdapter';
import { useNavigate } from 'react-router-dom';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import toast from 'react-hot-toast';
import { ButtonOptionGroup } from '../../../components/buttonOptionGroup/ButtonOptionGroup';
import adaptProfesionalesArray, { GET_ALL_PROFESIONALES } from '../../../data/graphQL/queries/Profesionales/getProfesionales';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import ClearIcon from '@mui/icons-material/Clear';
import { GET_ALL_TIPO_PROFESIONALES } from '../../../data/graphQL/queries/PlanCuidado/getTipoProfesionales';
import { adaptTipoProfesionalArray } from '../../../adapters/tipoProfesionalAdapter';
import { adaptAllTipoProfesionalEspecialidadArray } from "../../../adapters/allTipoProfesionalEspecialidadAdapter";
import { GET_ALL_TIPO_PROFESIONAL_ESPECIALIDAD } from "../../../data/graphQL/queries/PlanCuidado/getTipoProfesionalEspecialidad";

const storageId = sessionStorage.getItem('idUsuario');

const ESTADO_AC = 'Activos';
const ESTADO_IN = 'Inactivos';
const ESTADO_ALL = 'Todos';
const estadosOptions = [ESTADO_AC, ESTADO_IN, ESTADO_ALL];

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');

export const MonitoreoProfesionalesContent = () => {
  const navigate = useNavigate();

  const [especialidadesOptions, setEspecialidadesOptions] = useState([]);
  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [delegaciones, setDelegaciones] = useState([]);
  const [tipoProfesionales, setTipoProfesionales] = useState([]);

  const [allowedWrite, setAllowedWrite] = useState(false);

  const {
    profesionales,
    profesionalesLoading,
    totalCount,
    filters,
    handleChangeFilters,
    handleTextFilter,
    datosOriginal,
    setProfesionales
  } = useGetProfesionales(delegacionOrigen);

  const { data: tipoProfesionalesData } = useQuery(GET_ALL_TIPO_PROFESIONALES);

  useEffect(() => {
    if (!tipoProfesionalesData) return;
    setTipoProfesionales(adaptTipoProfesionalArray(tipoProfesionalesData));
  }, [tipoProfesionalesData]);

  const { data: dataTipoProfesionalEspecialidad } = useQuery(GET_ALL_TIPO_PROFESIONAL_ESPECIALIDAD);

  useEffect(() => {
    if (!dataTipoProfesionalEspecialidad) return;
    setEspecialidadesOptions(adaptAllTipoProfesionalEspecialidadArray(dataTipoProfesionalEspecialidad));
  }, [dataTipoProfesionalEspecialidad]);

  const initialColumns = [
    {
      headerName: '',
      sortable: false,
      field: 'action',
      flex: 30,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        if (!!row.activo) {
          return (
            <div onClick={() => handleClickEdit(row)} aria-hidden='true'
              className='item-option clickable-option'
              style={{ paddingTop: '10px' }}>

              <EditIcon className='edit-icon' style={{ fontSize: 20 }} />
            </div>);
        }
      }
    },
    {
      flex: 60,
      field: 'id',
      headerName: 'ID'
    },
    {
      flex: 200,
      field: 'nombreCompleto',
      headerName: 'Nombre'
    },
    {
      width: 200,
      field: 'idTipoProfesional',
      headerName: 'Tipo Profesional',
      valueGetter: (idTipoProfesional) => {
        return tipoProfesionales?.find((x) => x.id === idTipoProfesional)?.displayName;
      }
    },
    {
      flex: 200,
      field: 'especialidad',
      headerName: 'Especialidad',
      valueGetter: (especialidad) => {
        return especialidadesOptions?.find((x) => x.id === especialidad)?.displayName;
      }
    },
    {
      flex: 200,
      field: 'calificacionMonitoreo',
      headerName: 'Clasificación Monitoreo',
      align: 'center',
      renderCell: ({ row }) => {
        const { calificacionMonitoreo } = row;
        return calificacionMonitoreo || '-';
      }
    },
    {
      flex: 200,
      field: 'fechaProximoMonitoreo',
      headerName: 'Fecha Próximo Monitoreo',
      align: 'center',
      type: 'date',
      time: true
    },
    {
      flex: 200,
      field: 'poseeReclamos',
      align: "center",
      headerName: 'Posee Reclamos',
      renderCell: ({ row }) => {
        const { poseeReclamos } = row;
        return !!poseeReclamos ?
          "Si" :
          "No";
      }
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns, true), [delegaciones, profesionalesLoading]);

  const [getDelegaciones, { data: dataDelegaciones, loading: delegacionesLoading }] = useLazyQuery(GET_DELEGACIONES_BY_USUARIO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegaciones) return;
    setDelegaciones(adaptDelegacionReclamosArray(dataDelegaciones));
  }, [dataDelegaciones]);

  const [getDelegacionOrigen, { data: delegacionOrigenData }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!delegacionOrigenData) return;
    if (delegacionOrigenData.usuarioFull.success) {
      const deleg = parseInt(delegacionOrigenData.usuarioFull.valorEsperado.delegacionOrigen, 10);
      sessionStorage.setItem('delegacionOrigen', deleg);
      setDelegacionOrigen(deleg);

      if (filters.idDelegacion == 0)
        handleChangeFilters('idDelegacion', deleg);
    }
  }, [delegacionOrigenData]);

  useEffect(() => {
    if (!delegacionesLoading) {
      getDelegaciones({ variables: { idUsuario: JSON.parse(idUsuarioLoggeado, 10) } });
    }
    getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId, 10) } });
  }, []);

  const handleClickEdit = (row) => {
    navigate(`/asistencial/monitoreoProfesionales/modificacionProfesional`,
      {
        state: {
          idProfesional: row.id,
          allowedWrite: true,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleTextClean = () => {
    handleChangeFilters('txtFiltro', '');
    setProfesionales(datosOriginal);
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    handleChangeFilters(name, value);
  };

  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <Grid
        container
        spacing={2}
        sx={{ marginBottom: '25px' }}>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <TextField
            label='Buscar'
            name='txtFiltro'
            size='small'
            type='text'
            fullWidth
            variant='outlined'
            value={filters?.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            onKeyUp={handleTextFilter}
            sx={{ backgroundColor: 'white' }}
            InputProps={{
              endAdornment:
                <InputAdornment position='end'>
                  <SearchIcon
                    sx={{ cursor: 'pointer' }} />

                  <ClearIcon
                    onClick={handleTextClean}
                    sx={{ cursor: 'pointer' }} />

                </InputAdornment>

            }} />

        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Delegación</InputLabel>
            <Select
              labelId='select-delegacion'
              options={delegaciones}
              label='Delegación'
              onChange={handleChange}
              value={filters?.idDelegacion || undefined}
              name='idDelegacion'>

              {delegaciones && delegaciones.map((delegacion) => {
                return <MenuItem key={delegacion.id} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', flexWrap: 'wrap' }}>
            <ButtonOptionGroup
              key={filters?.estado}
              options={estadosOptions}
              onOptionClick={(v) => handleChange({ target: { name: 'estado', value: v } })}
              selectedOption={filters?.estado} />

          </Box>
        </Grid>
      </Grid>
      <DataGridGenerico
        columns={columns}
        totalCount={totalCount}
        rows={profesionales}
        toolbar={false}
        loading={profesionalesLoading} />

    </div>);

};

const useGetProfesionales = (delegacionOrigen) => {
  const [profesionales, setProfesionales] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: delegacionOrigen,
    estado: 0,
    txtFiltro: ''
  }, `ProfesionalesFilter`);

  const [getProfesionales, { data: dataProfesionales, error: errorProfesionales, loading: profesionalesLoading }] = useLazyQuery(GET_ALL_PROFESIONALES, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataProfesionales) return;
    setDatosOriginal(adaptProfesionalesArray(dataProfesionales));
  }, [dataProfesionales]);

  useEffect(() => {
    if (!errorProfesionales) return;
    toast.error('No se pudo cargar la tabla de profesionales, intentelo nuevamente más tarde.');
  }, [errorProfesionales]);

  useEffect(() => {
    if (profesionalesLoading) return;
    getProfesionales({
      variables: {
        idDelegacion: filters.idDelegacion === -1 ? null : filters.idDelegacion,
        activo: !!filters.estado && filters.estado === 2 ? null : !filters.estado
      }
    });
  }, [filters]);

  useEffect(() => {
    handleTextFilter();
  }, [datosOriginal]);

  const handleTextFilter = () => {
    const inputBuscar = filters?.txtFiltro?.toUpperCase();
    setProfesionales(datosOriginal?.filter((profesional) => {
      const id = profesional.id?.toString().toUpperCase();
      const nombre = profesional.nombre?.toString().toUpperCase();
      const apellido = profesional.apellido?.toUpperCase();
      const tipoProfesional = profesional.tipoProfesional?.toUpperCase() || '';

      return (
        id.includes(inputBuscar) ||
        nombre.includes(inputBuscar) ||
        apellido.includes(inputBuscar) ||
        tipoProfesional.includes(inputBuscar));

    }));
  };

  return {
    profesionales,
    profesionalesLoading,
    totalCount: profesionales?.profesionales?.length || 0,
    filters,
    handleChangeFilters,
    handleTextFilter,
    datosOriginal,
    setProfesionales
  };
};